import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, LocateFixed, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

const STORE_POS = [23.51122, 85.17228]; // Rani Super Market — Ginjo Thakurgaon

const pinIcon = L.divIcon({
  className: "",
  html: '<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#2563eb;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><div style="width:10px;height:10px;background:#fff;border-radius:50%"></div></div>',
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

function ClickHandler({ onPick }) {
  useMapEvents({ click: (e) => onPick([e.latlng.lat, e.latlng.lng]) });
  return null;
}

export default function LocationPicker({ open, onClose, initial, onSave }) {
  const [pos, setPos] = useState(null);
  const [addr, setAddr] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (open) {
      setAddr(null);
      setPos(initial?.lat && initial?.lng ? [initial.lat, initial.lng] : STORE_POS);
    }
  }, [open, initial]);

  useEffect(() => {
    if (!pos || !open) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setResolving(true);
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos[0]}&lon=${pos[1]}&format=json&addressdetails=1&accept-language=en`);
        const d = await r.json();
        const a = d.address || {};
        const line1 = [a.house_number, a.road || a.pedestrian || a.residential, a.suburb || a.neighbourhood || a.village || a.hamlet]
          .filter(Boolean).join(", ") || (d.display_name || "").split(",").slice(0, 2).join(",");
        setAddr({
          display: d.display_name || "",
          line1,
          city: a.city || a.town || a.village || a.county || "",
          state: a.state || "",
          pincode: a.postcode || "",
        });
      } catch { setAddr(null); }
      finally { setResolving(false); }
    }, 400);
    return () => clearTimeout(debounce.current);
  }, [pos, open]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location supported nahi hai"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (g) => { setPos([g.coords.latitude, g.coords.longitude]); setGeoBusy(false); },
      () => { toast.error("Location permission nahi mili — map pe pin se set karein"); setGeoBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="location-picker-modal">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-fade-up">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center"><MapPin size={16} className="text-blue-600" /></span>
            <div>
              <p className="text-sm font-extrabold text-slate-900">Set Delivery Location</p>
              <p className="text-[10px] text-slate-500">Map pe tap karein ya pin drag karein</p>
            </div>
          </div>
          <button data-testid="location-picker-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        <div className="h-72 sm:h-80 w-full relative z-0">
          {pos && (
            <MapContainer center={pos} zoom={16} style={{ height: "100%", width: "100%" }} key={pos.join(",")}>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <ClickHandler onPick={setPos} />
              <Marker position={pos} icon={pinIcon} draggable eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setPos([ll.lat, ll.lng]); } }} />
            </MapContainer>
          )}
        </div>

        <div className="px-4 py-3 space-y-3">
          <button data-testid="use-my-location" onClick={useMyLocation} disabled={geoBusy}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 transition-colors disabled:opacity-60">
            {geoBusy ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />} Use my current location (GPS)
          </button>

          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 min-h-[52px]" data-testid="resolved-address">
            {resolving ? (
              <p className="text-xs text-slate-400 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Address dhundha ja raha hai…</p>
            ) : addr ? (
              <>
                <p className="text-xs font-bold text-slate-800">{addr.line1 || addr.display}</p>
                <p className="text-[11px] text-slate-500">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</p>
              </>
            ) : (
              <p className="text-xs text-slate-400">Map pe apni location select karein</p>
            )}
          </div>

          <div className="flex gap-2 pb-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-300 text-slate-600 text-sm font-bold">Cancel</button>
            <button data-testid="location-picker-save" disabled={!addr || resolving}
              onClick={() => onSave({ ...addr, lat: pos[0], lng: pos[1], label: "Home" })}
              className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-bold transition-colors">
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
